// src/components/Leaderboard/Leaderboard.js
import React, { useEffect, useState, useContext } from 'react';
import axios from 'axios';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  Card,
  CardActionArea,
  CardContent,
  Avatar,
  Typography,
  CircularProgress,
  Stack,
  Divider,
  useTheme,
  useMediaQuery,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  alpha,
  Chip,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import { Info as InfoIcon, Star as StarIcon, RateReview as ReviewIcon, EmojiEvents as TrophyIcon } from '@mui/icons-material';
import API from '../config';
import { AuthContext } from '../../contexts/AuthContext';
import UpgradeModal from '../Subscription/UpgradeModal';
import BlurOverlay from '../Subscription/BlurOverlay';

export default function Leaderboard() {
  const { authData } = useContext(AuthContext);
  const token = authData?.access_token;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [creators, setCreators] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasActiveSubscription, setHasActiveSubscription] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch leaderboard
        const resp = await axios.get(`${API}/api/leaderboard`);
        setCreators(resp.data.leaderboard || []);

        // Fetch subscription status if user is logged in
        if (token) {
          const dashboardResponse = await axios.get(`${API}/api/dashboard`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          console.log('Leaderboard - Dashboard response:', dashboardResponse.data);
          console.log('Leaderboard - Subscription status:', dashboardResponse.data.profile?.subscriptionStatus);
          const subscriptionStatus = dashboardResponse.data.profile?.subscriptionStatus;
          const isActive = ['active', 'cancel_at_period_end', 'trialing', 'past_due', 'promo_active'].includes(subscriptionStatus);
          console.log('Leaderboard - Setting hasActiveSubscription to:', isActive);
          setHasActiveSubscription(isActive);
        }
      } catch (err) {
        console.error(err);
        setError('Failed to load leaderboard.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [token]);

  if (loading) {
    return (
      <Box sx={{ display:'flex', justifyContent:'center', mt:4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Typography color="error" align="center" sx={{ mt:4 }}>
        {error}
      </Typography>
    );
  }

  // Show blur overlay for non-subscribers
  if (!hasActiveSubscription) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <BlurOverlay
          onSubscribe={() => setShowUpgradeModal(true)}
          title="VIP Leaderboard Access"
          subtitle="Discover the highest-rated creators based on real subscriber feedback"
          showCount={false}
          message="See rankings, ratings, and reviews to find creators truly worth your money"
          customBenefits={[
            "See top creators ranked by subscriber satisfaction",
            "View detailed ratings and review counts",
            "Compare creators side-by-side",
            "Find the most recommended creators instantly"
          ]}
          centerBenefits={false}
        />
        <UpgradeModal
          open={showUpgradeModal}
          onClose={() => setShowUpgradeModal(false)}
        />
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py:8 }}>
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1, mb: 2 }}>
        <Typography variant="h4" component="h2" align="center">
          Top Creators
        </Typography>
        <IconButton
          onClick={() => setShowInfoModal(true)}
          size="small"
          sx={{
            color: 'text.secondary',
            '&:hover': {
              color: 'primary.main',
              bgcolor: alpha(theme.palette.primary.main, 0.1),
            },
          }}
        >
          <InfoIcon />
        </IconButton>
      </Box>
      <Divider sx={{ mb: 4 }} />

      {/* Top 3 Podium */}
      {creators.length > 0 && creators.slice(0, 3).length > 0 && (
        <Box sx={{ 
          width: '100%', 
          mb: 5,
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 2
        }}>
          {creators.slice(0, 3).map((c, idx) => (
            <Card
              key={c.id}
                component={RouterLink}
                to={`/creator/${c.id}`}
                elevation={0}
                sx={{
                  flex: { xs: '0 0 100%', sm: '1 1 0' },
                  minWidth: 0,
                  borderRadius: 3,
                  border: `2px solid ${idx === 0 
                    ? '#FFD700'
                    : idx === 1
                    ? '#C0C0C0'
                    : '#CD7F32'}`,
                  background: 'white',
                  p: 3,
                  textAlign: 'center',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'all 0.3s ease',
                  textDecoration: 'none',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: `0 12px 32px ${alpha(theme.palette.common.black, 0.12)}`,
                    zIndex: 1,
                  },
                }}
              >
                {/* Rank Badge on Avatar */}
                <Box sx={{ position: 'relative', display: 'flex', justifyContent: 'center', mb: 2.5 }}>
                  <Avatar
                    src={c.profilePicture || '/default-profile.png'}
                    alt={c.username}
                    sx={{
                      width: 120,
                      height: 120,
                      border: `4px solid ${idx === 0 
                        ? '#FFD700'
                        : idx === 1
                        ? '#C0C0C0'
                        : '#CD7F32'}`,
                      boxShadow: `0 6px 20px ${alpha(idx === 0 ? '#FFD700' : idx === 1 ? '#C0C0C0' : '#CD7F32', 0.4)}`,
                    }}
                  />
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: -12,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      width: 44,
                      height: 44,
                      borderRadius: '50%',
                      background: idx === 0 
                        ? 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)'
                        : idx === 1
                        ? 'linear-gradient(135deg, #C0C0C0 0%, #A8A8A8 100%)'
                        : 'linear-gradient(135deg, #CD7F32 0%, #B87333 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: `0 4px 12px ${alpha(theme.palette.common.black, 0.2)}`,
                      border: '3px solid white',
                    }}
                  >
                    <Typography variant="h6" sx={{ color: 'white', fontWeight: 800, fontSize: '1.25rem' }}>
                      {idx + 1}
                    </Typography>
                  </Box>
                </Box>

                <Typography variant="h6" fontWeight={700} sx={{ mb: 2.5, color: 'text.primary', fontSize: '1.2rem' }}>
                  {c.username}
                </Typography>

                {/* Stats */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75 }}>
                    <StarIcon sx={{ fontSize: 20, color: '#FFB800' }} />
                    <Typography variant="body1" sx={{ fontWeight: 600, fontSize: '0.95rem' }}>
                      {c.averageQuality.toFixed(1)} / 5
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75 }}>
                    <ReviewIcon sx={{ fontSize: 20, color: theme.palette.text.secondary }} />
                    <Typography variant="body1" sx={{ color: 'text.secondary', fontSize: '0.95rem' }}>
                      {c.numReviews} {c.numReviews === 1 ? 'review' : 'reviews'}
                    </Typography>
                  </Box>
                  <Box sx={{ 
                    mt: 'auto',
                    pt: 2,
                    px: 2.5,
                    py: 1.25,
                    borderRadius: 2.5,
                    bgcolor: alpha(theme.palette.primary.main, 0.12),
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1,
                    mx: 'auto',
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                  }}>
                    <TrophyIcon sx={{ fontSize: 20, color: theme.palette.primary.main }} />
                    <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main', fontSize: '1.1rem' }}>
                      {c.score.toFixed(1)}
                    </Typography>
                  </Box>
                </Box>
              </Card>
            ))}
        </Box>
      )}

      {/* Rest of Leaderboard - Table Format (Desktop/Tablet) or Card Format (Mobile) */}
      {creators.length > 3 && (
        <>
          <Typography variant="h6" fontWeight={600} sx={{ mb: 2.5, color: 'text.primary' }}>
            All Rankings
          </Typography>
          
          {/* Desktop/Tablet: Table Format */}
          <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
            <TableContainer 
              component={Paper} 
              elevation={0}
              sx={{ 
                borderRadius: 3,
                border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
                overflow: 'hidden',
              }}
            >
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.04) }}>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.875rem', color: 'text.secondary' }}>Rank</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.875rem', color: 'text.secondary' }}>Creator</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.875rem', color: 'text.secondary' }}>Review Count</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.875rem', color: 'text.secondary' }}>Average Review</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.875rem', color: 'text.secondary' }}>Score</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {creators.slice(3).map((c, idx) => (
                    <TableRow
                      key={c.id}
                      component={RouterLink}
                      to={`/creator/${c.id}`}
                      sx={{
                        textDecoration: 'none',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          bgcolor: alpha(theme.palette.primary.main, 0.04),
                        },
                        '&:last-child td': {
                          borderBottom: 0,
                        },
                      }}
                    >
                      <TableCell sx={{ fontWeight: 600, color: alpha(theme.palette.text.secondary, 0.7) }}>
                        {idx + 4}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Avatar
                            src={c.profilePicture || '/default-profile.png'}
                            alt={c.username}
                            sx={{ width: 40, height: 40 }}
                          />
                          <Typography variant="body2" fontWeight={600} sx={{ color: 'text.primary' }}>
                            {c.username}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                          <ReviewIcon sx={{ fontSize: 16, color: theme.palette.text.secondary }} />
                          <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 500 }}>
                            {c.numReviews}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                          <StarIcon sx={{ fontSize: 16, color: '#FFB800' }} />
                          <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 500 }}>
                            {c.averageQuality.toFixed(1)}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <Box sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 0.5,
                          px: 1.5,
                          py: 0.5,
                          borderRadius: 1.5,
                          bgcolor: alpha(theme.palette.primary.main, 0.1),
                        }}>
                          <TrophyIcon sx={{ fontSize: 16, color: theme.palette.primary.main }} />
                          <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                            {c.score.toFixed(1)}
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          {/* Mobile: Card Format */}
          <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
            <Stack spacing={2}>
              {creators.slice(3).map((c, idx) => (
                <Card
                  key={c.id}
                  component={RouterLink}
                  to={`/creator/${c.id}`}
                  elevation={0}
                  sx={{
                    borderRadius: 2,
                    border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
                    textDecoration: 'none',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      boxShadow: `0 4px 12px ${alpha(theme.palette.common.black, 0.08)}`,
                      transform: 'translateY(-2px)',
                    },
                  }}
                >
                  <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <Box sx={{ 
                        minWidth: 32,
                        height: 32,
                        borderRadius: '50%',
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: 'primary.main' }}>
                          {idx + 4}
                        </Typography>
                      </Box>
                      <Avatar
                        src={c.profilePicture || '/default-profile.png'}
                        alt={c.username}
                        sx={{ width: 48, height: 48 }}
                      />
                      <Typography variant="body1" fontWeight={600} sx={{ color: 'text.primary', flex: 1 }}>
                        {c.username}
                      </Typography>
                      <Box sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.5,
                        px: 1.5,
                        py: 0.75,
                        borderRadius: 2,
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                      }}>
                        <TrophyIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} />
                        <Typography variant="body2" sx={{ fontWeight: 700, color: 'primary.main' }}>
                          {c.score.toFixed(1)}
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 3, pl: 7 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <ReviewIcon sx={{ fontSize: 18, color: theme.palette.text.secondary }} />
                        <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                          {c.numReviews} {c.numReviews === 1 ? 'review' : 'reviews'}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <StarIcon sx={{ fontSize: 18, color: '#FFB800' }} />
                        <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                          {c.averageQuality.toFixed(1)} / 5
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          </Box>
        </>
      )}

      {/* Scoring Info Modal */}
      <Dialog
        open={showInfoModal}
        onClose={() => setShowInfoModal(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
          },
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <InfoIcon color="primary" />
            <Typography variant="h6">
              How Leaderboard Scoring Works
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body1" paragraph sx={{ mb: 2, fontWeight: 600 }}>
            Creators are ranked by their score, which balances both quality and quantity of reviews.
          </Typography>
          
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1, color: 'primary.main' }}>
              The Formula:
            </Typography>
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                mb: 2,
              }}
            >
              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                Score = Average Quality × log(Review Count + 1) × 10
              </Typography>
            </Box>
          </Box>

          <Typography variant="body2" paragraph sx={{ mb: 2 }}>
            <strong>What this means:</strong>
          </Typography>
          
          <Box component="ul" sx={{ pl: 2.5, mb: 2 }}>
            <Typography component="li" variant="body2" paragraph sx={{ mb: 1.5 }}>
              <strong>Average Quality</strong> is the average rating (1-5 stars) from all reviews. Higher ratings = higher score.
            </Typography>
            <Typography component="li" variant="body2" paragraph sx={{ mb: 1.5 }}>
              <strong>Review Count</strong> matters, but with diminishing returns. More reviews help, but quality is still the most important factor.
            </Typography>
            <Typography component="li" variant="body2" paragraph>
              A creator with <strong>5.0 average and 10 reviews</strong> will rank higher than one with <strong>3.0 average and 20 reviews</strong>.
            </Typography>
          </Box>

          <Box
            sx={{
              p: 2,
              borderRadius: 2,
              bgcolor: alpha(theme.palette.info.main, 0.1),
              border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`,
            }}
          >
            <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
              <strong>💡 Tip:</strong> The best creators have both high ratings AND multiple reviews, showing consistent quality over time.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setShowInfoModal(false)}
            variant="contained"
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            Got it!
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
